/**
 * Abstracts platform-specific PeerConnection API differences between browser WebRTC and node-datachannel.
 *
 * Browser WebRTC uses Promise-based APIs and property-style event handlers (`pc.onicecandidate = {}`),
 * while node-datachannel uses synchronous calls and method-style callbacks (`pc.onLocalCandidate {}`).
 */
package com.oasisfeng.invaluable.p2p.webrtc

import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlin.js.Promise

interface PeerConnectionAdapter {
    fun createPeerConnection(stunUrl: String, role: String): dynamic

    /**
     * Trigger offer SDP creation. Returns the local description (type, sdp).
     *
     * - Browser: `createOffer()` + `setLocalDescription()` → reads `localDescription`
     * - Node: Registers `onLocalDescription` callback; SDP is generated by a prior `createDataChannel()` call.
     *   **IMPORTANT**: Call `createDataChannel()` before this to trigger SDP generation on node-datachannel.
     */
    suspend fun createOfferSdp(pc: dynamic): SdpDescription

    /**
     * Set remote description and trigger answer SDP creation. Returns the local answer description.
     *
     * - Browser: `setRemoteDescription()` + `createAnswer()` + `setLocalDescription()` → reads `localDescription`
     * - Node: `setRemoteDescription(sdp, type)` triggers `onLocalDescription` callback automatically.
     */
    suspend fun createAnswerSdp(pc: dynamic, remoteType: String, remoteSdp: String): SdpDescription

    /** Set remote description without creating an answer (for applying a received answer). */
    suspend fun setRemoteDescription(pc: dynamic, type: String, sdp: String)

    /** Add a single remote ICE candidate. Browser wraps in Promise; node-datachannel is synchronous. */
    suspend fun addRemoteCandidate(pc: dynamic, candidate: String, mid: String)

    fun onLocalCandidate(pc: dynamic, callback: (candidate: String, mid: String) -> Unit)
    fun onGatheringStateChange(pc: dynamic, callback: (state: String) -> Unit)
    fun onConnectionStateChange(pc: dynamic, callback: (state: String) -> Unit)

    fun createDataChannel(pc: dynamic, label: String): dynamic
    fun onDataChannel(pc: dynamic, callback: (dc: dynamic) -> Unit)

    fun getGatheringState(pc: dynamic): String?
    fun close(pc: dynamic)

    companion object {
        /** Browser WebRTC PeerConnection adapter. */
        val Browser: PeerConnectionAdapter = BrowserPeerConnectionAdapter

        /** node-datachannel PeerConnection adapter. */
        val NodeDataChannel: PeerConnectionAdapter = NodePeerConnectionAdapter
    }
}

data class SdpDescription(val type: String, val sdp: String)

// ----- Browser implementation -----

private object BrowserPeerConnectionAdapter : PeerConnectionAdapter {

    override fun createPeerConnection(stunUrl: String, role: String): dynamic {
        val config = js("{}")
        config.iceServers = if (stunUrl.isBlank()) {
            emptyArray<dynamic>()
        } else {
            val server = js("{}")
            server.urls = stunUrl
            arrayOf(server)
        }
        return js("new RTCPeerConnection(config)")
    }

    override suspend fun createOfferSdp(pc: dynamic): SdpDescription {
        val offer = (pc.createOffer() as Promise<dynamic>).await()
        (pc.setLocalDescription(offer) as Promise<dynamic>).await()
        val desc = pc.localDescription ?: error("Missing localDescription after createOffer")
        return SdpDescription(desc.type as String, desc.sdp as String)
    }

    override suspend fun createAnswerSdp(pc: dynamic, remoteType: String, remoteSdp: String): SdpDescription {
        val remoteDesc = js("{}")
        remoteDesc.type = remoteType
        remoteDesc.sdp = remoteSdp
        (pc.setRemoteDescription(remoteDesc) as Promise<dynamic>).await()
        val answer = (pc.createAnswer() as Promise<dynamic>).await()
        (pc.setLocalDescription(answer) as Promise<dynamic>).await()
        val desc = pc.localDescription ?: error("Missing localDescription after createAnswer")
        return SdpDescription(desc.type as String, desc.sdp as String)
    }

    override suspend fun setRemoteDescription(pc: dynamic, type: String, sdp: String) {
        val desc = js("{}")
        desc.type = type
        desc.sdp = sdp
        (pc.setRemoteDescription(desc) as Promise<dynamic>).await()
    }

    override suspend fun addRemoteCandidate(pc: dynamic, candidate: String, mid: String) {
        // Apply mDNS loopback normalization for same-machine browser-to-browser testing
        val lines = WebRtcSignaling.normalizeCandidateLinesForLocalLoopback(candidate)
        for (line in lines) {
            val init = js("{}")
            init.candidate = line
            init.sdpMid = mid
            runCatching { (pc.addIceCandidate(init) as Promise<dynamic>).await() }
        }
    }

    override fun onLocalCandidate(pc: dynamic, callback: (String, String) -> Unit) {
        pc.onicecandidate = { event: dynamic ->
            val candidate = event?.candidate
            if (candidate != null) {
                callback(candidate.candidate as String, candidate.sdpMid as String)
            }
        }
    }

    override fun onGatheringStateChange(pc: dynamic, callback: (String) -> Unit) {
        pc.onicegatheringstatechange = { callback(pc.iceGatheringState as? String ?: "unknown") }
    }

    override fun onConnectionStateChange(pc: dynamic, callback: (String) -> Unit) {
        pc.onconnectionstatechange = { callback(pc.connectionState as? String ?: "unknown") }
    }

    override fun createDataChannel(pc: dynamic, label: String): dynamic =
        pc.createDataChannel(label, js("{ ordered: false, maxRetransmits: 1 }"))

    override fun onDataChannel(pc: dynamic, callback: (dynamic) -> Unit) {
        pc.ondatachannel = { event: dynamic -> callback(event.channel) }
    }

    override fun getGatheringState(pc: dynamic) = pc.iceGatheringState as? String

    override fun close(pc: dynamic) {
        if (pc == null) return
        pc.onicecandidate = null
        pc.onicegatheringstatechange = null
        pc.onconnectionstatechange = null
        pc.ondatachannel = null
        pc.close()
    }
}

// ----- node-datachannel implementation -----

private object NodePeerConnectionAdapter : PeerConnectionAdapter {

    override fun createPeerConnection(stunUrl: String, role: String): dynamic {
        val nodeDatachannel = js("eval('require')('node-datachannel')")
        val config = js("{}")
        config.iceServers = if (stunUrl.isBlank()) emptyArray<String>() else arrayOf(stunUrl)
        return js("new nodeDatachannel.PeerConnection(role, config)")
    }

    // createDataChannel (offerer) / setRemoteDescription (answerer) make node-datachannel generate the local description
    // asynchronously. We poll `localDescription()` rather than rely on the one-shot `onLocalDescription` event, whose
    // registration races with description generation (the event can fire before the handler is attached → a hang).
    override suspend fun createOfferSdp(pc: dynamic): SdpDescription = awaitLocalDescription(pc)

    override suspend fun createAnswerSdp(pc: dynamic, remoteType: String, remoteSdp: String): SdpDescription {
        pc.setRemoteDescription(remoteSdp, remoteType)
        return awaitLocalDescription(pc)
    }

    private suspend fun awaitLocalDescription(pc: dynamic): SdpDescription {
        repeat(250) {   // ~5s budget; loopback is ready within a tick or two
            val desc = runCatching { pc.localDescription() }.getOrNull()
            val type = desc?.type as? String
            val sdp = desc?.sdp as? String
            if (type != null && sdp != null) return SdpDescription(type, sdp)
            delay(20)
        }
        error("node-datachannel: local description not ready")
    }

    override suspend fun setRemoteDescription(pc: dynamic, type: String, sdp: String) {
        pc.setRemoteDescription(sdp, type)
    }

    override suspend fun addRemoteCandidate(pc: dynamic, candidate: String, mid: String) {
        pc.addRemoteCandidate(candidate, mid)
    }

    override fun onLocalCandidate(pc: dynamic, callback: (String, String) -> Unit) {
        pc.onLocalCandidate { candidate: String, mid: String -> callback(candidate, mid) }
    }

    override fun onGatheringStateChange(pc: dynamic, callback: (String) -> Unit) {
        pc.onGatheringStateChange { state: String -> callback(state) }
    }

    override fun onConnectionStateChange(pc: dynamic, callback: (String) -> Unit) {
        pc.onStateChange { state: String -> callback(state) }
    }

    override fun createDataChannel(pc: dynamic, label: String): dynamic = pc.createDataChannel(label)

    override fun onDataChannel(pc: dynamic, callback: (dynamic) -> Unit) {
        pc.onDataChannel { dc: dynamic -> callback(dc) }
    }

    // node-datachannel often finishes gathering before the local description is read, so the change handler can miss
    // "complete"; exposing the live state lets the caller complete the blob promptly instead of waiting out a timeout.
    override fun getGatheringState(pc: dynamic): String? = runCatching { pc.gatheringState() as? String }.getOrNull()

    override fun close(pc: dynamic) {
        if (pc == null) return
        runCatching { pc.onLocalDescription(null) }
        runCatching { pc.onLocalCandidate(null) }
        runCatching { pc.onGatheringStateChange(null) }
        runCatching { pc.onStateChange(null) }
        runCatching { pc.onDataChannel(null) }
        pc.close()
    }
}
